import { useState, type ReactNode } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { useTranslation } from 'react-i18next'
import { Check, ChevronRight, GripVertical, Plus, Trash2, TriangleAlert } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { NumberInput } from '@/components/ui/number-input'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import {
  REPORT_COMPONENTS,
  REPORT_TYPES,
  dateFormats,
  timeFormatOf,
  withTimeFormat,
  type ReportComponentParameters,
  type ReportFormat,
  type ReportType,
  type TimeFormat,
} from '@/services/reports/reportTypes'
import { BadgeToggleGroup } from '../BadgeToggleGroup'
import { FieldLabel } from '../FieldLabel'
import { SwitchField } from '../SwitchField'
import { ReportComponentThumbnail } from './ReportComponentThumbnail'
import { RichTextEditor } from '../RichTextEditor'
import { SortableTableRows } from '../SortableTableRows'

/** A component on the template, with a key that survives reordering. */
export interface EditableReportComponent {
  key: string
  componentId: string
  parameters: ReportComponentParameters
}

const CHART_TYPES = ['line', 'bar', 'column'] as const
const TIME_FORMATS: TimeFormat[] = ['HOUR24', 'AMPM', 'NONE']

interface ReportComponentsEditorProps {
  reportType: ReportType | ''
  reportFormat: ReportFormat
  value: EditableReportComponent[]
  onChange: (value: EditableReportComponent[]) => void
}

/**
 * The components a report is built from, top to bottom in the order the
 * report shows them. Each one opens to its own settings — heading, which stat
 * columns it shows, how many rows, how dates read.
 */
export function ReportComponentsEditor({
  reportType,
  reportFormat,
  value,
  onChange,
}: ReportComponentsEditorProps) {
  const { t } = useTranslation()
  const [adding, setAdding] = useState(false)
  // Newly added components open, so their settings are in view straight away.
  const [openKeys, setOpenKeys] = useState<Set<string>>(new Set())

  const reorder = (keys: string[]) => onChange(keys.map((key) => value.find((c) => c.key === key)!))

  const update = (key: string, parameters: ReportComponentParameters) =>
    onChange(value.map((c) => (c.key === key ? { ...c, parameters } : c)))

  const add = (componentIds: string[]) => {
    const added = componentIds.map((componentId) => ({
      key: crypto.randomUUID(),
      componentId,
      parameters: structuredClone(REPORT_COMPONENTS[componentId].parameters),
    }))
    setOpenKeys((keys) => new Set([...keys, ...added.map((c) => c.key)]))
    onChange([...value, ...added])
  }

  const toggleOpen = (key: string, open: boolean) =>
    setOpenKeys((keys) => {
      const next = new Set(keys)
      if (open) next.add(key)
      else next.delete(key)
      return next
    })

  if (!reportType) {
    return <p className="text-xs text-muted-foreground">{t('reportTemplates.selectTypeFirst')}</p>
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-(--ad-text-muted)">{t('reportTemplates.componentsHint')}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => setAdding(true)}>
          <Plus className="size-4" />
          {t('reportTemplates.addComponents')}
        </Button>
      </div>

      {value.length === 0 ? (
        <Empty className="border border-dashed">
          <EmptyHeader>
            <EmptyTitle>{t('reportTemplates.noComponents')}</EmptyTitle>
            <EmptyDescription>{t('reportTemplates.noComponentsHint')}</EmptyDescription>
          </EmptyHeader>
        </Empty>
      ) : (
        <ul className="flex flex-col gap-2">
          <SortableTableRows ids={value.map((c) => c.key)} onReorder={reorder}>
            {value.map((component) => {
              const definition = REPORT_COMPONENTS[component.componentId]
              const open = openKeys.has(component.key)
              const notInCsv = reportFormat === 'CSV' && !!definition && !definition.csv
              return (
                <SortableComponentItem
                  key={component.key}
                  id={component.key}
                  handleLabel={t('reportTemplates.dragToReorder')}
                >
                  {(handle) => (
                    <Collapsible open={open} onOpenChange={(o) => toggleOpen(component.key, o)}>
                      <div className="flex items-center gap-2 p-2">
                        {handle}
                        <CollapsibleTrigger asChild>
                          <button
                            type="button"
                            className="flex min-w-0 flex-1 cursor-pointer items-center gap-2 text-left"
                          >
                            <ChevronRight
                              className={cn(
                                'size-4 shrink-0 text-muted-foreground transition-transform',
                                open && 'rotate-90'
                              )}
                            />
                            <ReportComponentThumbnail
                              kind={definition?.kind ?? 'text'}
                              className="h-8 w-13"
                            />
                            <span className="flex min-w-0 flex-col">
                              <span className="truncate text-sm font-medium">
                                {componentName(component.componentId, t)}
                              </span>
                              {component.parameters.heading && (
                                <span className="truncate text-xs text-muted-foreground">
                                  {component.parameters.heading}
                                </span>
                              )}
                            </span>
                          </button>
                        </CollapsibleTrigger>
                        {notInCsv && (
                          <span
                            className="flex items-center gap-1 text-xs text-(--ad-badge-amber)"
                            title={t('reportTemplates.notInCsv')}
                          >
                            <TriangleAlert className="size-3.5" />
                            {t('reportTemplates.notInCsvShort')}
                          </span>
                        )}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon-sm"
                          aria-label={t('common.remove')}
                          onClick={() => onChange(value.filter((c) => c.key !== component.key))}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </div>
                      <CollapsibleContent>
                        <div className="border-t p-3">
                          <ComponentSettings
                            component={component}
                            onChange={(parameters) => update(component.key, parameters)}
                          />
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </SortableComponentItem>
              )
            })}
          </SortableTableRows>
        </ul>
      )}

      {adding && (
        <AddReportComponentsDialog
          reportType={reportType}
          reportFormat={reportFormat}
          onClose={() => setAdding(false)}
          onAdd={add}
        />
      )}
    </div>
  )
}

/* ── Dragging ────────────────────────────────────────────────────────── */

/**
 * A component card that can be dragged by its grip. The grip sits inside the
 * card's header row, so it is handed to the card's content to place.
 */
function SortableComponentItem({
  id,
  handleLabel,
  children,
}: {
  id: string
  handleLabel: string
  children: (handle: ReactNode) => ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })
  const handle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      aria-label={handleLabel}
      className="flex cursor-grab touch-none items-center text-(--ad-text-faint) hover:text-(--ad-text) active:cursor-grabbing"
      {...attributes}
      {...listeners}
    >
      <GripVertical className="size-4" />
    </button>
  )
  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Translate.toString(transform), transition }}
      className={cn(
        'rounded-(--ad-radius) border bg-(--ad-surface)',
        isDragging && 'relative z-10 shadow-md'
      )}
    >
      {children(handle)}
    </li>
  )
}

function componentName(id: string, t: (key: string, options?: Record<string, unknown>) => string) {
  return t(`reportComponents.${id}.name`, { defaultValue: id })
}

/* ── One component's settings ────────────────────────────────────────── */

function ComponentSettings({
  component,
  onChange,
}: {
  component: EditableReportComponent
  onChange: (parameters: ReportComponentParameters) => void
}) {
  const { t } = useTranslation()
  const definition = REPORT_COMPONENTS[component.componentId]
  const defaults = definition?.parameters ?? {}
  const params = component.parameters
  const has = (key: string) => key in defaults
  const set = (patch: ReportComponentParameters) => onChange({ ...params, ...patch })
  const fieldLabel = (field: string) =>
    t(`reportFields.${definition?.translationOverrides?.[field] ?? field}`, { defaultValue: field })
  const id = (name: string) => `rc-${component.key}-${name}`

  if ('text' in defaults) {
    return (
      <RichTextEditor
        value={params.text ?? ''}
        onChange={(text) => set({ text })}
        placeholder={t('reportTemplates.textPlaceholder')}
      />
    )
  }

  const lineItemFormat = params.dateTimeFormatLineItem ?? ''
  const timeFormat = timeFormatOf(lineItemFormat)

  return (
    <div className="flex flex-col gap-4">
      <div className="field-grid">
        {has('heading') && (
          <div className="p-field full">
            <FieldLabel
              htmlFor={id('heading')}
              tooltip={has('limit') ? t('reportTemplates.headingLimitHelp') : undefined}
            >
              {t('reportTemplates.heading')}
            </FieldLabel>
            <Input
              id={id('heading')}
              value={params.heading ?? ''}
              onChange={(e) => set({ heading: e.target.value })}
            />
          </div>
        )}
        {has('limit') && (
          <div className="p-field">
            <FieldLabel htmlFor={id('limit')}>{t('reportTemplates.limit')}</FieldLabel>
            <NumberInput
              id={id('limit')}
              min={1}
              value={params.limit ?? null}
              onChange={(limit) => set({ limit: limit ?? undefined })}
            />
          </div>
        )}
        {has('chartType') && (
          <div className="p-field">
            <FieldLabel>{t('reportTemplates.chartType')}</FieldLabel>
            <Select
              value={params.chartType ?? 'line'}
              onValueChange={(v) => set({ chartType: v as ReportComponentParameters['chartType'] })}
            >
              <SelectTrigger>
                <SelectValue>
                  {t(`reportTemplates.chartTypes.${params.chartType ?? 'line'}`)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {CHART_TYPES.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(`reportTemplates.chartTypes.${type}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {has('height') && (
          <div className="p-field">
            <FieldLabel htmlFor={id('height')} hint={t('reportTemplates.pixels')}>
              {t('reportTemplates.chartHeight')}
            </FieldLabel>
            <NumberInput
              id={id('height')}
              min={1}
              value={params.height ?? null}
              onChange={(height) => set({ height: height ?? undefined })}
            />
          </div>
        )}
        {has('sharedYAxis') && (
          <div className="p-field full">
            <FieldLabel>{t('reportTemplates.yAxis')}</FieldLabel>
            <RadioGroup
              value={params.sharedYAxis ? 'shared' : 'multiple'}
              onValueChange={(v) => set({ sharedYAxis: v === 'shared' })}
              className="flex flex-wrap gap-4"
            >
              <Label className="font-normal">
                <RadioGroupItem value="multiple" />
                {t('reportTemplates.yAxisMultiple')}
              </Label>
              <Label className="font-normal">
                <RadioGroupItem value="shared" />
                {t('reportTemplates.yAxisShared')}
              </Label>
            </RadioGroup>
          </div>
        )}
        {has('dateFormat') && (
          <div className="p-field">
            <FieldLabel>{t('reportTemplates.dateFormat')}</FieldLabel>
            <DateFormatSelect
              value={params.dateFormat ?? ''}
              time="NONE"
              onChange={(dateFormat) => set({ dateFormat })}
            />
          </div>
        )}
        {has('dateTimeFormatLineItem') && (
          <div className="p-field">
            <FieldLabel>{t('reportTemplates.lineItemDateFormat')}</FieldLabel>
            <DateFormatSelect
              value={lineItemFormat}
              time={timeFormat}
              onChange={(dateTimeFormatLineItem) => set({ dateTimeFormatLineItem })}
            />
            {lineItemFormat && (
              <RadioGroup
                value={timeFormat}
                onValueChange={(v) =>
                  set({ dateTimeFormatLineItem: withTimeFormat(lineItemFormat, v as TimeFormat) })
                }
                className="flex flex-wrap gap-4 pt-1"
              >
                {TIME_FORMATS.map((format) => (
                  <Label key={format} className="font-normal">
                    <RadioGroupItem value={format} />
                    {t(`reportTemplates.timeFormats.${format}`)}
                  </Label>
                ))}
              </RadioGroup>
            )}
          </div>
        )}
      </div>

      {(['hideCreativesWithoutStats', 'hideAllCreatives', 'onlySites'] as const)
        .filter(has)
        .map((flag) => (
          <SwitchField
            key={flag}
            label={t(`reportTemplates.flags.${flag}`)}
            checked={!!params[flag]}
            onCheckedChange={(checked) => set({ [flag]: checked })}
          />
        ))}

      {defaults.fields && (
        <div className="flex flex-col gap-2">
          <FieldLabel hint={t('reportTemplates.columnsHint')}>
            {t('reportTemplates.columns')}
          </FieldLabel>
          <BadgeToggleGroup
            options={Object.keys(defaults.fields).map((field) => ({
              value: field,
              label: fieldLabel(field),
            }))}
            value={Object.keys(defaults.fields).filter((field) => params.fields?.[field])}
            onChange={(on) =>
              set({
                fields: Object.fromEntries(
                  Object.keys(defaults.fields!).map((field) => [field, on.includes(field)])
                ),
              })
            }
          />
        </div>
      )}
    </div>
  )
}

/** Stands in for "the user's own locale" — Radix items need a non-empty value. */
const LOCALE_FORMAT = '__locale__'

function DateFormatSelect({
  value,
  time,
  onChange,
}: {
  value: string
  time: TimeFormat
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  const options = dateFormats(time)
  const label = (format: string, example: string) =>
    format ? `${example} (${format})` : t('reportTemplates.localeDateFormat')
  const current = options.find((o) => o.format === value)
  return (
    <Select
      value={value || LOCALE_FORMAT}
      onValueChange={(v) => onChange(v === LOCALE_FORMAT ? '' : v)}
    >
      <SelectTrigger>
        <SelectValue>{current ? label(current.format, current.example) : value}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {options.map((o) => (
          <SelectItem key={o.format || LOCALE_FORMAT} value={o.format || LOCALE_FORMAT}>
            {label(o.format, o.example)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/* ── Adding components ───────────────────────────────────────────────── */

function AddReportComponentsDialog({
  reportType,
  reportFormat,
  onClose,
  onAdd,
}: {
  reportType: ReportType
  reportFormat: ReportFormat
  onClose: () => void
  onAdd: (componentIds: string[]) => void
}) {
  const { t } = useTranslation()
  const [selected, setSelected] = useState<string[]>([])
  // A CSV report is one table per component, so only components that render
  // to a table are offered.
  const offered = REPORT_TYPES[reportType].components.filter(
    (id) => reportFormat !== 'CSV' || REPORT_COMPONENTS[id].csv
  )

  const toggle = (id: string) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('reportTemplates.addComponentsTitle')}</DialogTitle>
          <DialogDescription>{t('reportTemplates.addComponentsHelp')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 sm:grid-cols-2">
          {offered.map((id) => {
            const isSelected = selected.includes(id)
            return (
              <button
                key={id}
                type="button"
                aria-pressed={isSelected}
                onClick={() => toggle(id)}
                className={cn(
                  'flex cursor-pointer items-start gap-3 rounded-(--ad-radius) border p-2.5 text-left transition-colors hover:bg-accent',
                  isSelected && 'border-(--ad-primary) bg-(--ad-primary-soft)'
                )}
              >
                <ReportComponentThumbnail kind={REPORT_COMPONENTS[id].kind} className="h-12 w-20" />
                <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <span className="flex items-center justify-between gap-2 text-xs font-semibold">
                    {componentName(id, t)}
                    {isSelected && <Check className="size-3.5 shrink-0 text-(--ad-primary)" />}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t(`reportComponents.${id}.description`, { defaultValue: '' })}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            disabled={selected.length === 0}
            onClick={() => {
              onAdd(selected)
              onClose()
            }}
          >
            <Plus className="size-4" />
            {t('reportTemplates.addSelected', { count: selected.length })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
